/**
 * Public Questionnaire Page
 *
 * This page allows external vendors to complete questionnaires
 * without authentication, using a secure access token.
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../lib/api'
import {
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  Building2,
  Save,
  Send,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'

interface QuestionnaireItem {
  id: string
  question_number: number
  question: string
  question_type: string
  required: boolean
  help_text?: string | null
  choices?: string[] | null
  response?: string | null
  answered_at?: number | null
  category?: string | null
}

interface QuestionnaireData {
  id: string
  title: string
  description?: string | null
  due_date?: number | null
  sender_company?: string | null
  vendor_contact_name?: string | null
  vendor_name?: string | null
}

interface Progress {
  answered: number
  total: number
  percentage: number
}

export default function PublicQuestionnaire() {
  const { token } = useParams<{ token: string }>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireData | null>(null)
  const [items, setItems] = useState<QuestionnaireItem[]>([])
  const [progress, setProgress] = useState<Progress>({ answered: 0, total: 0, percentage: 0 })
  const [responses, setResponses] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())

  // Load questionnaire data
  useEffect(() => {
    async function loadQuestionnaire() {
      if (!token) {
        setError('Invalid questionnaire link')
        setLoading(false)
        return
      }

      try {
        const result = await api.getExternalQuestionnaire(token)
        if (!result.success) {
          setError(result.error || 'Failed to load questionnaire')
          setErrorCode(result.error_code || null)
          setLoading(false)
          return
        }

        if (result.completed) {
          setSubmitted(true)
        }

        setQuestionnaire(result.questionnaire)
        setItems(result.items || [])
        setProgress(result.progress || { answered: 0, total: 0, percentage: 0 })

        // Initialize responses from existing answers
        const initialResponses: Record<string, string> = {}
        result.items?.forEach((item) => {
          if (item.response) {
            initialResponses[item.id] = item.response
          }
        })
        setResponses(initialResponses)

        // Expand all categories by default
        const categories = new Set(result.items?.map((item) => item.category || 'General') || [])
        setExpandedCategories(categories)
      } catch (err) {
        setError('Failed to load questionnaire. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    loadQuestionnaire()
  }, [token])

  // Auto-save responses every 30 seconds
  useEffect(() => {
    if (submitted || Object.keys(responses).length === 0) return

    const interval = setInterval(async () => {
      await saveResponses(false)
    }, 30000)

    return () => clearInterval(interval)
  }, [responses, submitted])

  // Save responses
  const saveResponses = useCallback(async (showFeedback = true) => {
    if (!token || Object.keys(responses).length === 0) return

    if (showFeedback) setSaving(true)

    try {
      const responseArray = Object.entries(responses).map(([item_id, response]) => ({
        item_id,
        response,
      }))

      await api.submitExternalQuestionnaire(token, responseArray, false)
      setLastSaved(new Date())

      // Update progress
      const answeredCount = Object.values(responses).filter((r) => r && r.trim()).length
      setProgress({
        answered: answeredCount,
        total: items.length,
        percentage: items.length > 0 ? Math.round((answeredCount / items.length) * 100) : 0,
      })
    } catch (err) {
      console.error('Failed to save responses:', err)
    } finally {
      if (showFeedback) setSaving(false)
    }
  }, [token, responses, items.length])

  // Submit questionnaire
  const handleSubmit = async () => {
    if (!token) return

    // Check required questions
    const unanswered = items.filter(
      (item) => item.required && (!responses[item.id] || !responses[item.id].trim())
    )

    if (unanswered.length > 0) {
      setError(`Please answer all required questions. ${unanswered.length} required question(s) unanswered.`)
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const responseArray = Object.entries(responses).map(([item_id, response]) => ({
        item_id,
        response,
      }))

      const result = await api.submitExternalQuestionnaire(token, responseArray, true)

      if (!result.success) {
        setError(result.error || 'Failed to submit questionnaire')
        return
      }

      setSubmitted(true)
    } catch (err) {
      setError('Failed to submit questionnaire. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // Update response
  const updateResponse = (itemId: string, value: string) => {
    setResponses((prev) => ({ ...prev, [itemId]: value }))
  }

  // Toggle category
  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }

  // Group items by category
  const groupedItems = items.reduce((acc, item) => {
    const category = item.category || 'General'
    if (!acc[category]) acc[category] = []
    acc[category].push(item)
    return acc
  }, {} as Record<string, QuestionnaireItem[]>)

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading questionnaire...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error && !questionnaire) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {errorCode === 'EXPIRED' ? 'Questionnaire Expired' :
             errorCode === 'COMPLETED' ? 'Already Completed' :
             'Unable to Load Questionnaire'}
          </h1>
          <p className="text-gray-600">{error}</p>
          {errorCode === 'EXPIRED' && (
            <p className="mt-4 text-sm text-gray-500">
              Please contact the organization that sent you this questionnaire to request a new link.
            </p>
          )}
        </div>
      </div>
    )
  }

  // Submitted state
  if (submitted) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600">
            Your questionnaire has been submitted successfully.
          </p>
          {questionnaire?.sender_company && (
            <p className="mt-4 text-sm text-gray-500">
              {questionnaire.sender_company} will review your responses.
            </p>
          )}
        </div>
      </div>
    )
  }

  // Main questionnaire view
  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <FileText className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  {questionnaire?.title || 'Security Questionnaire'}
                </h1>
                {questionnaire?.sender_company && (
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Building2 className="h-3.5 w-3.5" />
                    Requested by {questionnaire.sender_company}
                  </p>
                )}
              </div>
            </div>

            {/* Progress */}
            <div className="flex items-center gap-4">
              {lastSaved && (
                <span className="text-xs text-gray-400">
                  Saved {lastSaved.toLocaleTimeString()}
                </span>
              )}
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">
                  {progress.answered} / {progress.total}
                </div>
                <div className="text-xs text-gray-500">completed</div>
              </div>
              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-600 transition-all duration-300"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Description */}
        {questionnaire?.description && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
            <p className="text-gray-700">{questionnaire.description}</p>
          </div>
        )}

        {/* Due date warning */}
        {questionnaire?.due_date && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <Clock className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <p className="text-amber-800">
              Please complete this questionnaire by{' '}
              <span className="font-medium">
                {new Date(questionnaire.due_date).toLocaleDateString()}
              </span>
            </p>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Questions by category */}
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([category, categoryItems]) => (
            <div key={category} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Category header */}
              <button
                onClick={() => toggleCategory(category)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{category}</span>
                  <span className="text-sm text-gray-500">
                    ({categoryItems.filter((i) => responses[i.id]?.trim()).length} / {categoryItems.length})
                  </span>
                </div>
                {expandedCategories.has(category) ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>

              {/* Category questions */}
              {expandedCategories.has(category) && (
                <div className="divide-y divide-gray-100">
                  {categoryItems.map((item) => (
                    <div key={item.id} className="p-4">
                      <label className="block">
                        <div className="flex items-start gap-2 mb-2">
                          <span className="text-sm font-medium text-gray-500">
                            Q{item.question_number}.
                          </span>
                          <div className="flex-1">
                            <span className="text-gray-900">
                              {item.question}
                              {item.required && (
                                <span className="text-red-500 ml-1">*</span>
                              )}
                            </span>
                            {item.help_text && (
                              <p className="text-sm text-gray-500 mt-1">{item.help_text}</p>
                            )}
                          </div>
                        </div>

                        {/* Response input based on question type */}
                        {item.question_type === 'boolean' && (
                          <div className="ml-6 flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`q-${item.id}`}
                                value="Yes"
                                checked={responses[item.id] === 'Yes'}
                                onChange={(e) => updateResponse(item.id, e.target.value)}
                                className="text-primary-600 focus:ring-primary-500"
                              />
                              <span>Yes</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`q-${item.id}`}
                                value="No"
                                checked={responses[item.id] === 'No'}
                                onChange={(e) => updateResponse(item.id, e.target.value)}
                                className="text-primary-600 focus:ring-primary-500"
                              />
                              <span>No</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`q-${item.id}`}
                                value="N/A"
                                checked={responses[item.id] === 'N/A'}
                                onChange={(e) => updateResponse(item.id, e.target.value)}
                                className="text-primary-600 focus:ring-primary-500"
                              />
                              <span>N/A</span>
                            </label>
                          </div>
                        )}

                        {item.question_type === 'rating' && (
                          <div className="ml-6 flex gap-2">
                            {[1, 2, 3, 4, 5].map((rating) => (
                              <button
                                key={rating}
                                type="button"
                                onClick={() => updateResponse(item.id, String(rating))}
                                className={`w-10 h-10 rounded-lg border-2 font-medium transition-colors ${
                                  responses[item.id] === String(rating)
                                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                {rating}
                              </button>
                            ))}
                          </div>
                        )}

                        {item.question_type === 'multiple_choice' && item.choices && (
                          <div className="ml-6 space-y-2">
                            {item.choices.map((choice) => (
                              <label key={choice} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`q-${item.id}`}
                                  value={choice}
                                  checked={responses[item.id] === choice}
                                  onChange={(e) => updateResponse(item.id, e.target.value)}
                                  className="text-primary-600 focus:ring-primary-500"
                                />
                                <span>{choice}</span>
                              </label>
                            ))}
                          </div>
                        )}

                        {(item.question_type === 'text' || item.question_type === 'long_text') && (
                          <textarea
                            value={responses[item.id] || ''}
                            onChange={(e) => updateResponse(item.id, e.target.value)}
                            rows={item.question_type === 'long_text' ? 4 : 2}
                            className="ml-6 w-full max-w-2xl mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Enter your response..."
                          />
                        )}

                        {!['boolean', 'rating', 'multiple_choice', 'text', 'long_text'].includes(item.question_type) && (
                          <textarea
                            value={responses[item.id] || ''}
                            onChange={(e) => updateResponse(item.id, e.target.value)}
                            rows={2}
                            className="ml-6 w-full max-w-2xl mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Enter your response..."
                          />
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Action buttons */}
        <div className="mt-8 flex items-center justify-between">
          <button
            onClick={() => saveResponses(true)}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Saving...' : 'Save Progress'}
          </button>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {submitting ? 'Submitting...' : 'Submit Questionnaire'}
          </button>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Your responses are automatically saved every 30 seconds.</p>
          <p className="mt-1">
            Powered by <span className="font-medium text-gray-700">Clarantis</span>
          </p>
        </div>
      </main>
    </div>
  )
}
